import React, { useEffect, useState, useMemo } from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CRow,
  CCol,
  CSpinner,
  CButton,
  CListGroup,
  CListGroupItem,
	CTable,
	CTableHead,
	CTableRow,
	CTableHeaderCell,
	CTableDataCell,
	CBadge,
	CTableBody,
	CInputGroup,
	CFormInput,
	CPagination,
	CPaginationItem,
} from "@coreui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilPencil, cilSearch, cilSortAscending, cilSortDescending } from "@coreui/icons";
import { getAll, getProductDetails } from "src/services/Products";

function ProductDetails() {
    const [productDetails, setProductDetails] = useState(null);
		const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // Sorting state
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Filtering state
    const [filterText, setFilterText] = useState('');
    
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    async function fetchProductDetails() {
        try {
            const res = await getProductDetails(id);
            setProductDetails(res.product);
						setStores(res.stores || []);
        } catch (error) {
            console.error("Error fetching product details:", error);
        } finally {
            setLoading(false);
        }
    }

		useEffect(() => {
			fetchProductDetails();
		}, [id]);

		// Filter and sort stores
		const filteredAndSortedStores = useMemo(() => {
			let filtered = stores;
			
			// Apply filter
			if (filterText) {
				filtered = stores.filter(store => 
					store.name.toLowerCase().includes(filterText.toLowerCase()) ||
					store.stock.toString().includes(filterText)
				);
			}
			
			// Apply sorting
			filtered.sort((a, b) => {
				let aValue = a[sortField];
				let bValue = b[sortField];
				
				// Handle numeric sorting for stock
				if (sortField === 'stock') {
					aValue = parseInt(aValue) || 0;
					bValue = parseInt(bValue) || 0;
				} else {
					// String sorting
					aValue = aValue?.toString().toLowerCase() || '';
					bValue = bValue?.toString().toLowerCase() || '';
				}
				
				if (sortDirection === 'asc') {
					return aValue > bValue ? 1 : -1;
				} else {
					return aValue < bValue ? 1 : -1;
				}
			});
			
			return filtered;
		}, [stores, filterText, sortField, sortDirection]);

		// Paginate filtered and sorted stores
		const paginatedStores = useMemo(() => {
			const startIndex = (currentPage - 1) * itemsPerPage;
			const endIndex = startIndex + itemsPerPage;
			return filteredAndSortedStores.slice(startIndex, endIndex);
		}, [filteredAndSortedStores, currentPage, itemsPerPage]);

		// Calculate total pages
		const totalPages = Math.ceil(filteredAndSortedStores.length / itemsPerPage);

		// Handle sorting
		const handleSort = (field) => {
			if (sortField === field) {
				setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
			} else {
				setSortField(field);
				setSortDirection('asc');
			}
			setCurrentPage(1); // Reset to first page when sorting
		};

		// Handle filter change
		const handleFilterChange = (e) => {
			setFilterText(e.target.value);
			setCurrentPage(1); // Reset to first page when filtering
		};

		// Handle page change
		const handlePageChange = (page) => {
			setCurrentPage(page);
		};

		// Get sort icon
		const getSortIcon = (field) => {
			if (sortField !== field) return null;
			return sortDirection === 'asc' ? cilSortAscending : cilSortDescending;
		};

		if (loading) {
			return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <CSpinner color="primary" />
        </div>
      );
		}

		if (!productDetails) {
			return <div>Product not found</div>;
		}

  return (
    <div style={{ color: "black" }}>
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <h4 className="mb-0">Product Details</h4>
              <small className="text-muted">ID: {productDetails.id}</small>
            </CCol>
            <CCol md={6} className="text-end">
              <CButton
                color="light"
                onClick={() =>
                  navigate(-1, {
                    state: {
                      page: location.state?.fromPage || 0,
                      pageSize: location.state?.fromPageSize || 10,
                    },
                  })
                }
                className="me-2"
              >
                <CIcon icon={cilArrowLeft} className="me-2" />
                Back
              </CButton>
              <CButton
                color="primary"
                onClick={() => navigate(`/product/actions/${id}`)}
              >
                <CIcon icon={cilPencil} className="me-2" />
                Edit Store
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CRow className="mb-4">
            <CCol>
              <CCard className="mb-4">
                <CCardHeader>
                  <h5>Product Information</h5>
                </CCardHeader>
                <CCardBody>
                  <CListGroup>
                    <CListGroupItem>
                      <strong>Category:</strong> {productDetails.categoryName}
                    </CListGroupItem>
                    <CListGroupItem>
                      <strong>SKU:</strong> {productDetails.sku}
                    </CListGroupItem>
                    <CListGroupItem>
                      <strong>Name:</strong> {productDetails.name}
                    </CListGroupItem>
                  </CListGroup>
                </CCardBody>
              </CCard>
              <CCard>
                <CCardHeader>
                  <h5>Stock Availability by Store</h5>
                </CCardHeader>
                <CCardBody>
                  {/* Filter Input */}
                  <CInputGroup className="mb-3">
                    <CFormInput
                      placeholder="Search stores or stock..."
                      value={filterText}
                      onChange={handleFilterChange}
                    />
                    <CButton color="secondary" variant="outline">
                      <CIcon icon={cilSearch} />
                    </CButton>
                  </CInputGroup>

                  {/* Results Info */}
                  <div className="mb-3">
                    <small className="text-muted">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedStores.length)} of {filteredAndSortedStores.length} stores
                    </small>
                  </div>

                  <CTable hover striped>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell 
                          scope="col"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSort('name')}
                        >
                          <div className="d-flex align-items-center">
                            Store Name
                            {getSortIcon('name') && (
                              <CIcon icon={getSortIcon('name')} className="ms-2" size="sm" />
                            )}
                          </div>
                        </CTableHeaderCell>
                        <CTableHeaderCell 
                          scope="col"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSort('stock')}
                        >
                          <div className="d-flex align-items-center">
                            Stock
                            {getSortIcon('stock') && (
                              <CIcon icon={getSortIcon('stock')} className="ms-2" size="sm" />
                            )}
                          </div>
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {paginatedStores.map((store, i) => (
                        <CTableRow key={i}>
                          <CTableDataCell
                            onClick={() =>
                              navigate(`/stores/details/${store.id}`)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {store.name}
                          </CTableDataCell>
                          <CTableDataCell>{store.stock}</CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <CPagination>
                        <CPaginationItem
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          Previous
                        </CPaginationItem>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <CPaginationItem
                            key={page}
                            active={page === currentPage}
                            onClick={() => handlePageChange(page)}
                            style={{ cursor: 'pointer' }}
                          >
                            {page}
                          </CPaginationItem>
                        ))}
                        
                        <CPaginationItem
                          disabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                          style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        >
                          Next
                        </CPaginationItem>
                      </CPagination>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
}

export default ProductDetails;